import type { KeyboardEvent } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeftRight, ArrowUpFromLine } from 'lucide-react'
import { TokenIcon } from './holdings-table'
import { PlatformMark } from './platform-chip'
import { KRAKEN_ASSET_MINTS, type KrakenSellAsset } from './kraken-sell-modal'
import { useTRPC } from '@/lib/trpc'
import { useKrakenAvailability } from '@/lib/pool'
import { tokenLogo, USD_LOGO } from '@/lib/default-tokens'
import { formatTokenAmountShort, formatUsd, formatUsdShort } from '@/lib/format'
import { CardFigure, HEADER_BTN, HEADER_ICON } from './card-header'
import { SkeletonRows } from './skeleton-rows'
import { HoldingsSettings } from './holdings-settings'
import { KRAKEN_FILTER } from '@/lib/holdings-filter'

const ASSETS: KrakenSellAsset[] = ['SOL', 'USDC', 'USDT']

/**
 * What sits on Kraken (John, Nov 1 2026): SOL, USDC and USDT and the USD they
 * turn into. Sell and Withdraw sit in the header as on the Holdings card
 * (Nov 3 2026: the per-row buttons went); Sell opens on the asset worth the
 * most, a row tap opens its own asset, the USD row the bank withdrawal. Only
 * while Kraken is wired up.
 */
export function KrakenCard({ onSell, onWithdraw }: { onSell: (asset: KrakenSellAsset) => void; /** the USD row: send dollars to a bank (John, Nov 2 2026) */ onWithdraw: () => void }) {
  const trpc = useTRPC()
  const { known, available } = useKrakenAvailability()
  // every 10 s, and at once when a withdrawal lands (the activity feed re-reads it); Kraken itself credits a deposit a few seconds after it is on chain
  const balances = useQuery({ ...trpc.kraken.balances.queryOptions(), enabled: available, refetchInterval: 10_000, refetchOnWindowFocus: true })
  const filter = KRAKEN_FILTER.use()
  // the card holds its place with its skeleton while the answer is on its way, like the lists above and below it (John,
  // Nov 5 2026: a refresh had no Kraken section until it dropped in); gone only once Kraken is known to be off
  if (known && !available) return null
  const data = balances.data
  // SOL always has its row, even at zero (John, Nov 2 2026); USDC and USDT only while held
  const rows = data ? ASSETS.filter((asset) => asset === 'SOL' || data[asset].total > 0).map((asset) => ({ asset, ...data[asset] })) : []
  const usd = data?.USD.total ?? 0
  const valueOf = (row: { total: number; priceUsd: number | null }) => (row.priceUsd ? row.total * row.priceUsd : 0)
  const total = rows.reduce((sum, row) => sum + valueOf(row), usd)
  // the card's own small-balance line (John, Nov 4 2026), the Holdings control with a store of its own: SOL stays, the rest and the USD row go under it
  const minUsd = filter.hideSmall ? filter.minUsd : 0
  const visible = rows.filter((row) => row.asset === 'SOL' || valueOf(row) >= minUsd)
  const showUsd = usd >= minUsd
  // the header's Sell opens on the asset worth the most on Kraken, SOL when nothing is there
  const best = rows.reduce<(typeof rows)[number] | null>((top, row) => (top === null || valueOf(row) > valueOf(top) ? row : top), null)?.asset ?? 'SOL'
  const rowKeys = (open: () => void) => (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      open()
    }
  }
  return (
    <section className="card">
      {/* the total right after the name and the two flows on the right, as the Holdings header (John, Nov 3 2026) */}
      <div className="relative flex flex-wrap items-center justify-between gap-x-2 gap-y-2 px-4 py-3 sm:px-6">
        <div className="flex min-w-0 items-center gap-x-2 sm:gap-x-3">
          <div className="flex items-center gap-2.5">
            <PlatformMark id="kraken" size="size-5 sm:size-6" />
            <h2 className="card-title">Kraken</h2>
          </div>
          <span className="card-divider" aria-hidden="true" />
          {data ? <CardFigure usd={total} /> : <span className="skeleton inline-block h-5 w-16 sm:h-6 sm:w-28" />}
          <HoldingsSettings store={KRAKEN_FILTER} label="Kraken settings" />
        </div>
        <div className="ml-auto flex shrink-0 items-center gap-1 sm:gap-2">
          <button type="button" className={`${HEADER_BTN} btn-primary`} onClick={() => onSell(best)} aria-label="Sell" title="Sell">
            <ArrowLeftRight className={HEADER_ICON} />
            <span className="hidden lg:inline">Sell</span>
          </button>
          <button type="button" className={`${HEADER_BTN} btn-secondary`} onClick={onWithdraw} aria-label="Withdraw" title="Withdraw">
            <ArrowUpFromLine className={HEADER_ICON} />
            <span className="hidden lg:inline">Withdraw</span>
          </button>
        </div>
      </div>
      <div className="border-t border-line">
        {balances.isPending ? (
          // rows in the list's shape, like the other lists (John, Nov 4 2026: "missing a kraken skeleton loader"): the coins and the USD row
          <SkeletonRows rows={3} columns={2} />
        ) : balances.isError && !data ? (
          <p className="px-4 py-4 text-sm text-negative sm:px-6">Kraken balances could not be loaded.</p>
        ) : (
          <div className="overflow-x-auto overscroll-x-contain">
            {/* a refused read (Kraken's rate limit) keeps the last rows on screen with a quiet note, never a red line in their
                place (John, Nov 5 2026: "I keep facing this kraken error occasionally") */}
            {balances.isError || data?.stale ? <p className="px-4 pt-2 text-xs text-muted sm:px-6">Some balances may be out of date</p> : null}
            <table className="w-full text-sm">
              <tbody>
                {visible.map((row) => (
                  // a row opens the sell for its own asset, balance or not (the dialog shows what is there)
                  <tr
                    key={row.asset}
                    className="cursor-pointer border-t border-line hover:bg-surface-2 first:border-t-0"
                    onClick={() => onSell(row.asset)}
                    onKeyDown={rowKeys(() => onSell(row.asset))}
                    tabIndex={0}
                    role="button"
                  >
                    <td className="px-4 py-3 sm:px-6">
                      <div className="flex items-center gap-3">
                        <TokenIcon symbol={row.asset} imageUrl={tokenLogo(KRAKEN_ASSET_MINTS[row.asset])} />
                        <div className="min-w-0">
                          <div className="font-medium">{row.asset}</div>
                          {/* seen by Kraken, not credited yet: the gap between a withdrawal reading done and the balance moving */}
                          {row.incoming > 0 ? <div className="text-xs text-muted">+{formatTokenAmountShort(row.incoming, row.asset)} on its way</div> : null}
                        </div>
                      </div>
                    </td>
                    <td className={`px-4 py-3 text-right tabular-nums ${row.total === 0 ? 'text-muted' : ''}`}>{formatTokenAmountShort(row.total, row.asset)}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-muted sm:px-6">{row.priceUsd ? formatUsdShort(row.total * row.priceUsd) : '–'}</td>
                  </tr>
                ))}
                {/* the USD row opens the bank withdrawal (John, Nov 2 2026) */}
                {showUsd ? (
                <tr
                  className="cursor-pointer border-t border-line hover:bg-surface-2 first:border-t-0"
                  onClick={onWithdraw}
                  onKeyDown={rowKeys(onWithdraw)}
                  tabIndex={0}
                  role="button"
                >
                  <td className="px-4 py-3 sm:px-6">
                    <div className="flex items-center gap-3">
                      <TokenIcon symbol="USD" imageUrl={USD_LOGO} />
                      <span className="font-medium">USD</span>
                    </div>
                  </td>
                  <td className={`px-4 py-3 text-right tabular-nums ${usd === 0 ? 'text-muted' : ''}`}>{formatUsd(usd)}</td>
                  <td className="px-4 py-3 sm:px-6" />
                </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  )
}
